import React, {
  useContext,
  useMemo,
  useReducer,
  useRef,
  useState,
  type JSX,
} from 'react';
import _ from 'lodash';
import Select from '../Select';
import Button from '../Button';
import { X } from 'lucide-react';
import { nanoid } from 'nanoid';

import styles from './Modal.module.css';
import '../../app/style/react-select.css';

const MODAL_STARTING_Z_INDEX = 10;
export const ModalContext = React.createContext<Modal | undefined>(undefined);

/**
 * Creates a JSX container for displaying any modals in the modal stack
 * @param children JSX children
 * @returns JSX container for displaying any modals in the modal stack
 */
function ModalContainer({
  children,
}: React.PropsWithChildren<any>): JSX.Element {
  const context: Modal | undefined = useContext(ModalContext);

  return (
    <>
      {children}
      {context &&
        !_.isEmpty(context) &&
        context.modals.map((modal) => (
          <ModalDisplay
            key={modal.id}
            content={modal.content}
            id={modal.id}
            index={modal.getStackIndex()}
            onBackgroundClick={modal.close}
          />
        ))}
    </>
  );
}

interface ModalStack {
  /** Function to get the existing modals within the stack */
  getModals: () => ModalEntry[];
  /** Function to push a new modal onto the stack */
  push: (modal: ModalEntry) => ModalEntry;
  /** Function to pop the top modal from the stack */
  pop: () => ModalEntry;
  /** Function to remove the given modal from the stack */
  remove: (modal: ModalEntry) => ModalEntry;
  /** Function to pop all modals including and after the given modal within the stack */
  slice: (modal: ModalEntry) => ModalEntry;
  /** Function to replace an existing modal within the stack */
  update: (modal: ModalEntry, newModal: ModalEntry) => ModalEntry;
  /** Function to get the number of entries in the stack */
  size: () => number;
  /** Function to remove all modal entries from the stack */
  clear: () => ModalEntry[];
  /** Function to check whether or not the stack is empty */
  isEmpty: () => boolean;
  /** Function to get the index of an existing modal within the stack */
  findModalIndex: (modal: ModalEntry | string) => number;
}

interface ModalEntry {
  /** Reference to the modal stack that this modal entry will live in */
  stack: ModalStack;
  /** ID of the modal */
  id: string;
  /** JSX content of the modal */
  content: JSX.Element;
  /** Function to close the modal */
  close: () => ModalEntry;
  /** Function to get the index of the modal in the modal stack */
  getStackIndex: () => number;
}

/**
 * Returns a context provider for the modal context containing methods for interacting with the modal stack
 * @returns Provider for the modal context containing methods for interacting with the modal stack
 */
export function ModalStackContextProvider({
  children,
}: React.PropsWithChildren<any>): JSX.Element {
  const entriesRef = useRef<ModalEntry[]>([]);
  // Changes to entriesRef don't trigger re-renders, so calling this reducer will
  // force a re-render with the entriesRef.current value. The flipped boolean
  // itself (not just the dispatch) is also what the `stack`/`modal` memos below
  // key off of, so a re-render caused by an actual stack mutation is
  // distinguishable from an unrelated re-render of some ancestor.
  const [stackVersion, forceUpdate] = useReducer((x) => !x, true);

  // Every method here only closes over `entriesRef`/`forceUpdate`, both of
  // which React guarantees are stable for the component's lifetime, so this
  // only ever needs to be built once.
  const stack: ModalStack = useMemo(() => {
    function getModals(): ModalEntry[] {
      return entriesRef.current;
    }

    /** Push a new modal onto the top of the stack */
    function push(modal: ModalEntry): ModalEntry {
      if (entriesRef.current.find((entry) => entry.id === modal.id)) {
        const error = `Modal with an ID ${modal.id} already exists`;
        console.error(error);
        throw new Error(error);
      }

      entriesRef.current = entriesRef.current.concat(modal);
      forceUpdate();
      return modal;
    }

    /** Pop the modal at the top of the stack */
    function pop(): ModalEntry {
      if (_.isEmpty(entriesRef.current)) {
        const error = `No modals can be deleted because the modal stack is empty`;
        console.error(error);
        throw new Error(error);
      }

      const lastEntry = entriesRef.current[entriesRef.current.length - 1];
      entriesRef.current = entriesRef.current.filter(
        (entry) => entry.id !== lastEntry.id,
      );
      forceUpdate();
      return lastEntry;
    }

    /** Remove a particular modal from the stack */
    function remove(modal: ModalEntry): ModalEntry {
      const entryIndex = entriesRef.current.findIndex(
        (entry) => entry.id === modal.id,
      );
      if (entryIndex === -1) {
        const error = `Modal with ID ${modal.id} cannot be removed because it doesn't exist in the modal stack`;
        console.error(error);
        throw new Error(error);
      }
      entriesRef.current = entriesRef.current.filter(
        (_entry, i) => i !== entryIndex,
      );
      forceUpdate();
      return modal;
    }

    /** Pop all modals including and after the given modal */
    function slice(modal: ModalEntry): ModalEntry {
      const entryIndex = entriesRef.current.findIndex(
        (entry) => entry.id === modal.id,
      );
      if (entryIndex === -1) {
        const error = `Cannot slice the stack because modal with ID ${modal.id} doesn't exist in the modal stack`;
        console.error(error);
        throw new Error(error);
      }
      entriesRef.current = entriesRef.current.slice(0, entryIndex);
      forceUpdate();
      return modal;
    }

    /** Update a particular modal from the stack */
    function update(modal: ModalEntry, newModal: ModalEntry): ModalEntry {
      const entryIndex = entriesRef.current.findIndex(
        (entry) => entry.id === modal.id,
      );
      if (entryIndex === -1) {
        const error = `Modal with ID ${modal.id} cannot be updated because it doesn't exist in the modal stack`;
        console.error(error);
        throw new Error(error);
      }

      const updatedEntries = [
        ...entriesRef.current.slice(0, entryIndex),
        newModal,
        ...entriesRef.current.slice(entryIndex + 1),
      ];

      entriesRef.current = updatedEntries;
      forceUpdate();
      return newModal;
    }

    /** Count the number of modals in the stack */
    function size(): number {
      return entriesRef.current.length;
    }

    /** Remove all modals from the stack */
    function clear(): ModalEntry[] {
      const existingEntries = entriesRef.current;
      entriesRef.current = [];
      forceUpdate();
      return existingEntries;
    }

    /** Check whether the stack has any modals */
    function isEmpty(): boolean {
      return _.isEmpty(entriesRef.current);
    }

    /** Find the index in the stack of a particular modal */
    function findModalIndex(modal: ModalEntry | string): number {
      const id = typeof modal === 'string' ? modal : modal.id;
      return entriesRef.current.findIndex((entry) => entry.id === id);
    }

    return {
      push,
      pop,
      remove,
      slice,
      update,
      size,
      clear,
      isEmpty,
      findModalIndex,
      getModals,
    };
  }, []);

  // `createModalAccess` snapshots `stack.getModals()` into a plain `modals`
  // array at call time (not a live getter), so this has to recompute exactly
  // when the stack actually mutates -- `stackVersion` flips on every
  // push/pop/remove/slice/update/clear above, which is exactly that signal.
  // Depending on `stack` too is for correctness even though it's permanently
  // stable after the memo above. oxlint can't see this dependency: the
  // factory never textually reads `stackVersion`, only what it's a proxy for
  // (entriesRef.current, via the closures inside `stack`).
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const modal = useMemo(() => createModalAccess(stack), [stack, stackVersion]);

  return (
    <ModalContext.Provider value={modal}>
      {children}
      <ModalContainer />
    </ModalContext.Provider>
  );
}

export interface ModalPushProps {
  /**
   * Content to be displayed on the modal
   *
   * To display a standard modal, populate the relevant `DefaultContentProps`.
   * To display an entirely custom modal with custom styling, pass in the custom modal's JSX content.
   * */
  content: StandardModalContentProps | JSX.Element;
  /** Unique ID assigned to the modal to distinguish from other modals in the stack */
  id?: string;
  /** Whether the modal should close when the modal background is clicked  */
  closeOnBackgroundClick?: boolean;
}

export type ReactModalButton = React.ReactElement<
  React.ButtonHTMLAttributes<HTMLButtonElement>
>;

/**
 * Creates an abstracted object for cleanly interfacing with the modal stack
 *
 * The `Modal` object created here will be made available via ModalContext. It will define the entry point for using modals in the code.
 * @param stack Reference to the modal stack that will be interacted with
 * @returns Object for cleanly interfacing with the modal stack
 */
function createModalAccess(stack: ModalStack): Modal {
  function find(modal: ModalEntry | string): ModalEntry | undefined {
    const id = typeof modal === 'string' ? modal : modal.id;
    return stack.getModals().find((entry) => entry.id === id);
  }

  function push(props: ModalPushProps): ModalEntry {
    const entry = createModalEntry({ stack, ...props });
    stack.push(entry);
    return entry;
  }

  function pop(modal?: ModalEntry | string): ModalEntry {
    if (!modal) {
      return stack.pop();
    }
    const index = stack.findModalIndex(modal);
    if (index === -1) {
      const error = 'Modal cannot be deleted because it cannot be found';
      console.error(error);
      throw new Error(error);
    }
    return stack.slice(stack.getModals()[index]);
  }

  function popAll(): ModalEntry[] {
    return stack.clear();
  }

  function size(): number {
    return stack.size();
  }

  return {
    modals: stack.getModals(),
    getModals: stack.getModals,
    find,
    push,
    pop,
    popAll,
    size,
  };
}

interface CreateModalEntryProps {
  /** Reference to the modal stack that this modal entry will live in */
  stack: ModalStack;
  /** Content of the modal */
  content: StandardModalContentProps | JSX.Element;
  /**
   * ID of the modal
   *
   * If not specified, will be assigned a random ID
   * */
  id?: string;
  /** Whether the modal should be closed when the background is clicked */
  closeOnBackgroundClick?: boolean;
}

/**
 * Creates a new modal at the top of the modal-stack
 * @returns New modal on top of the modal-stack
 */
function createModalEntry(props: CreateModalEntryProps): ModalEntry {
  const {
    stack,
    content,
    id = nanoid(),
    closeOnBackgroundClick = true,
  } = props;

  function close(): ModalEntry {
    const modalEntry = stack.getModals().find((entry) => entry.id === id);

    if (!modalEntry) {
      const error = `Modal with ID ${id} cannot be deleted because it doesn't exist in the modal stack`;
      console.error(error);
      throw new Error(error);
    }

    stack.remove(modalEntry);
    return modalEntry;
  }

  function getStackIndex(): number {
    return stack.findModalIndex(id);
  }

  if ('body' in content) {
    if (!content.onOk) {
      // Unless otherwise specified, pressing 'OK' will simply close the modal
      content.onOk = close;
    }
    if (!content.onCancel) {
      // Unless otherwise specified, pressing 'Cancel' will simply close the modal
      content.onCancel = close;
    }
  }

  const modalDisplay: JSX.Element = (
    <ModalDisplay
      content={content}
      index={getStackIndex()}
      id={id}
      onBackgroundClick={closeOnBackgroundClick ? close : () => {}}
    />
  );

  return { id, content: modalDisplay, close, stack, getStackIndex };
}

interface StandardModalContentProps {
  /** Body text of the modal */
  body: string | JSX.Element;
  /** Title to appear at the top of the modal */
  title?: string;
  /**
   * Content of the OK button
   *
   * If `null`, no OK button will be displayed
   */
  okButton?: string | ReactModalButton | null | undefined;
  /**
   * Content of the Cancel button
   *
   * If `null`, no Cancel button will be displayed
   */
  cancelButton?: string | ReactModalButton | null | undefined;
  /**
   * Callback function to run whenever the OK button is clicked
   *
   * If the `content` parameter contains input fields, they can be put into an object and used as a parameter in the callback
   * */
  onOk?: (inputValues?: any) => void;
  /** Callback function to run whenever the Cancel button is clicked */
  onCancel?: () => void;
  /** Array of inputs to show on the modal */
  inputs?: ModalInput[];
  /**
   * Whether an 'X' should appear on the modal to close it
   *
   * If unspecified, will default to `false`
   * */
  showCloseX?: boolean;
}

interface ModalInput {
  /** ID of the input which will designate it within the `inputValues` object */
  id: string;
  /** Label above the input */
  label: string;
  /**
   * Type of input being entered
   *
   * Note: If no `rows` param is passed within TextInputParams, the input will be a simple text input. If a `rows` param is passed in, the input will be a textarea input.
   *  */
  params: DropdownSelectParams | TextInputParams;
  /** Whether or not the input is required for clicking the OK button */
  required: boolean;
}

interface TextInputParams {
  /** Max number of characters allowed */
  maxCharacters?: number;
  /** Number of rows' height */
  rows?: number;
}

interface DropdownSelectParams {
  /** Dropdown options */
  options: {
    value: string;
    label: string;
  }[];
}

interface ModalDisplayProps {
  /**
   * Content to be displayed on the modal
   *
   * To display a standard modal, populate the relevant `DefaultContentProps`.
   * To display an entirely custom modal with custom styling, pass in the custom modal's JSX content.
   * */
  content: StandardModalContentProps | JSX.Element;
  /** Unique ID assigned to the modal to distinguish from other modals in the stack */
  id: string;
  /** index of the modal within the modal stack */
  index: number;
  /** Whether the modal should close when the modal background is clicked  */
  onBackgroundClick: () => void;
}

/** UI component to display a single modal */
export function ModalDisplay(props: ModalDisplayProps): JSX.Element {
  const { content, id, index, onBackgroundClick } = props;
  const z = MODAL_STARTING_Z_INDEX + index;

  // Create an object with all of the inputs' ids and default to empty string values
  // This `inputValues` object's values will get populated as input values are entered
  const [inputValues, setInputValues] = useState<any>(
    'inputs' in content
      ? content.inputs?.reduce(
          (aggr, input) => ({ ...aggr, [input.id]: '' }),
          {},
        )
      : {},
  );

  function buildInputs(inputs: ModalInput[]): React.ReactNode {
    return (
      <div className={styles.inputs}>
        {inputs?.map((input) => {
          if (input.params && 'options' in input.params) {
            return (
              <label key={input.id}>
                <span>{input.label}</span>
                <Select
                  value={input.params.options.find(
                    (option) => option.value === inputValues[input.id],
                  )}
                  options={input.params.options}
                  onChange={(option: { value: string } | null) =>
                    setInputValues({
                      ...inputValues,
                      [input.id]: option?.value,
                    })
                  }
                />
              </label>
            );
          }
          if (input.params && 'rows' in input.params) {
            return (
              <label key={input.id}>
                <span>{input.label}</span>
                <textarea
                  rows={input.params.rows || 1}
                  value={inputValues[input.id]}
                  onChange={(e) =>
                    setInputValues({
                      ...inputValues,
                      [input.id]: e.target.value,
                    })
                  }
                />
              </label>
            );
          }
          return (
            <label key={input.id}>
              <span>{input.label}</span>
              <input
                value={inputValues[input.id]}
                onChange={(e) =>
                  setInputValues({ ...inputValues, [input.id]: e.target.value })
                }
              />
            </label>
          );
        })}
      </div>
    );
  }

  const requiredInputsComplete =
    'inputs' in content && !_.isEmpty(content.inputs)
      ? content.inputs
          ?.filter((input) => input.required)
          .every((input) => !_.isEmpty(inputValues[input.id]))
      : true;

  function buildOkButton(
    okButton?: string | ReactModalButton | null,
    onOk?: (inputValues?: any) => void,
  ): React.ReactNode {
    if (okButton === undefined) {
      return (
        <Button onClick={onOk} disabled={!requiredInputsComplete}>
          OK
        </Button>
      );
    } else if (typeof okButton === 'string') {
      return (
        <Button onClick={onOk} disabled={!requiredInputsComplete}>
          {okButton}
        </Button>
      );
    } else if (React.isValidElement(okButton)) {
      const onClick = onOk ? () => onOk(inputValues) : okButton.props.onClick;
      return React.cloneElement(okButton, {
        onClick,
        disabled: !requiredInputsComplete,
      });
    }
    return null;
  }

  function buildCancelButton(
    cancelButton?: string | ReactModalButton | null,
    onCancel?: () => void,
  ): React.ReactNode {
    if (cancelButton === undefined) {
      return (
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      );
    } else if (typeof cancelButton === 'string') {
      return (
        <Button variant="outline" onClick={onCancel}>
          {cancelButton}
        </Button>
      );
    } else if (React.isValidElement(cancelButton)) {
      const onClick = onCancel ? onCancel : cancelButton.props.onClick;
      return React.cloneElement(cancelButton, { onClick });
    }
    return null;
  }

  return (
    <div
      key={id}
      style={{ zIndex: z }}
      onClick={onBackgroundClick}
      className={styles.background}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={styles['content-box']}
      >
        {'body' in content ? (
          <>
            {content.title && (
              <h3 className={styles.title}>
                <span>{content.title}</span>
                {content.showCloseX ? (
                  <span onClick={content.onCancel} className={styles.x}>
                    <X />
                  </span>
                ) : null}
              </h3>
            )}
            <div className={styles.content}>
              {content.body}
              {content.inputs && buildInputs(content.inputs)}
            </div>
            <div className={styles.actions}>
              {buildCancelButton(content.cancelButton, content.onCancel)}
              {buildOkButton(content.okButton, content.onOk)}
            </div>
          </>
        ) : (
          content
        )}
      </div>
    </div>
  );
}

interface Modal {
  /** Modals currently on the modal stack */
  modals: ModalEntry[];
  getModals: () => ModalEntry[];
  find: (modal: ModalEntry | string) => ModalEntry | undefined;
  /** Function to push a new modal to the top of the stack */
  push: (props: ModalPushProps) => ModalEntry;
  /**
   * Function to pop a single modal from the stack
   *
   * If the `modal` parameter is specified, this will pop all modals up through the specified modal.
   * If no `modal` parameter is specified, this will pop the modal at the top of the stack.
   * */
  pop: (modal?: ModalEntry | string) => ModalEntry;
  /** Function to pop all modals from the modal stack */
  popAll: () => ModalEntry[];
  /** Function to get the size of the modal stack */
  size: () => number;
}

export default Modal;
